import React from "react";
import { Flex, Space, notification, Spin } from "antd";
import FilterPills from "../../widgets/FilterPills";
import { useSearchParams } from "react-router-dom";
import Headline from "./Headline";
import LocationsApi from "../../api-services/locations-api.service";
import { Country } from "../../interfaces/country.interface";
import SeriesApi from "../../api-services/series-api.service";

import ListingsV1 from "../../components/shared/v1/Listings";
import LocationDropdown from "../../components/shared/LocationDropdown";
import Listings from "../../components/shared/Listings";
import HomeLink from "../../widgets/HomeLink";
import { format, lastDayOfMonth } from "date-fns";

const bannerPath = '/iceholidays-assets/images/gd-listing-page.png';

function withSearchParams(Component) {
  return props => <Component {...props} searchParams={useSearchParams()} />;
}

class ListingPage extends React.Component <{searchParams}> {
  locationsApi = new LocationsApi;
  seriesApi = new SeriesApi;

  state = {
    loading: true,
    countries: [],
    selectedCountry: {name: "", cities: [], cover: ""},
    searchParamsObj: {keyword: "", year: "", months: null as string[] | null, location_id: null, brands: ["GD_Standard"]},
    itineraries: [],
    itineraryB2cV2: false,
    location: {name: "", cover: ""},
    breadcrumbs: [{ title: <HomeLink/>}]
  }

  constructor(props:any){
    super(props);
  }

  componentDidMount() {
    this.setSearchParamsObj();

    this.locationsApi.getCountries()
      .then(({locations}) => {
        if(locations.length > 0){
          var selectedCountry = locations[0];
          const searchParams = this.state.searchParamsObj;
          const country = locations.find(loc =>
              (searchParams.keyword != "" && loc.name.toLowerCase() == searchParams.keyword.toLowerCase())
              || (searchParams.location_id != null && loc.id == searchParams.location_id));
  
          if(country) selectedCountry = country;
          this.setState({countries: locations, selectedCountry})
  
          const locationId = searchParams.location_id;
          if(locationId != null) this.getLocation(locationId);
  
          this.getItineraries(searchParams);
        } else {
          this.setState({loading: false});
        }
      })
      .catch(() => {
        notification.error({ message: 'An error occured while loading countries.'});
        this.setState({loading: false});
      });
    
  }

  private setSearchParamsObj(){
    const [searchParams] = this.props.searchParams;
    const {searchParamsObj} = this.state;
    searchParams.keys().forEach(key => {
      const value = searchParams.get(key);
      if(key == "date"){
        const monthDate = new Date(value);
        const months = [format(monthDate, 'yyyy-MM-01'), format(lastDayOfMonth(monthDate), 'yyyy-MM-dd')]
        searchParamsObj["year"] = monthDate.getFullYear().toString();
        searchParamsObj["months"] = months;
      }
      else{
        searchParamsObj[key] = value;
      }
    });
    
    const {breadcrumbs} = this.state;
    breadcrumbs[1] = {title: this.searchParamsToText()};
    this.setState({searchParamsObj, breadcrumbs});
  }


  searchParamsToText() {
    var {keyword, year, months} = this.state.searchParamsObj;
    var month = "";
    if(months != null) month = format(new Date(months[0]), 'MMM')
    const monthYear = (month || year) && `${month} ${year}`;
    const text = [(this.state.location?.name || keyword), monthYear].filter(val => val).join(", ");
    return <span>{ text } </span>
  }

  getLocation(locationId){
    //resets the breadcrumbs
    this.setState({breadcrumbs: [{ title: <HomeLink/>}]});

    this.locationsApi.getLocation(locationId)
      .then(locationData => {
        var locationCrumbs = this.state.breadcrumbs;
        if(locationData.ancestry?.length && locationData.ancestry?.length > 0){
          locationData.ancestry.forEach(a => {
            locationCrumbs.push({title: <span key={a.id}> {a.name} </span>})
          });
          const country = locationData.ancestry[0].id;
          this.selectCountry(country);
        }
        locationCrumbs.push({title: <span>{locationData.name}</span>});
        this.setState({location: locationData, breadcrumbs: locationCrumbs})
      })
      .catch(() => {
        notification.error({ message: 'An error occured while loading location.'});
      });
  }

  getItineraries(searchParams){
    this.setState({loading: true});

    this.seriesApi.getItineraries(searchParams)
      .then(itinerariesData => {
        this.setBreadcrumbs();
        this.setState({itineraries: itinerariesData.itineraries, itineraryB2cV2: itinerariesData.itinerary_b2c_v2})
      })
      .finally(()=>this.setState({loading: false}))
      .catch(() => {
        notification.error({ message: 'An error occured while loading itineraries.'});
      });
  }

  setBreadcrumbs(){
    var breadcrumbs = this.state.breadcrumbs;
    if(breadcrumbs.length > 1){
      const {keyword, ...noKeyword} = this.state.searchParamsObj;
      const allHasValues = Object.entries(noKeyword).every(o => o[1] != "" && o[1] != null);
      if(allHasValues){
        const searchResultsCrumb = {title: <span>Search results</span>};
        if(breadcrumbs.length > 2){
          breadcrumbs[breadcrumbs.length-2] = searchResultsCrumb;
        }else{
          breadcrumbs.splice(1, 0, searchResultsCrumb);
        }
      } 

      breadcrumbs[breadcrumbs.length-1] = {title: this.searchParamsToText()};
    }

    this.setState({breadcrumbs});
  }

  selectCountry = (country) => {
    const selectedCountry:any = this.state.countries.find((c:Country) => c.id == country);
    if(selectedCountry){
      const searchParamsObj = this.state.searchParamsObj;
      this.setState({selectedCountry, searchParamsObj});
    }
  }

  search(selected, locationChanged=false){
    // re-call location api if changed
    if(locationChanged) this.getLocation(selected.location_id);

    this.getItineraries(selected);
  }


  render(){
    const {loading, countries, selectedCountry, searchParamsObj, itineraries, location, itineraryB2cV2, breadcrumbs } = this.state;

    return <div id="listing-page">
    
      <Headline bannerImage={location.cover || selectedCountry.cover || bannerPath} breadcrumbs={breadcrumbs}>
        <div id="listing-page_header">
          <LocationDropdown initialValue={selectedCountry?.name} locations={countries} selectLocation={this.selectCountry}/>
        </div>
      </Headline>
      
      <div id="listing-page_body">
        <Space size={12} direction="vertical">
          <FilterPills locations={selectedCountry.cities} locationsHeader="Region" bindLabel="name" initialValue={searchParamsObj} selectFilter={(selected, locationChanged) => this.search(selected, locationChanged)}></FilterPills>
        </Space>

        <h1>{this.searchParamsToText()}</h1>

        <Spin size="large" className="loader" spinning={loading}/>
         {!loading &&
          (itineraryB2cV2 ? (
            <Listings itineraries={itineraries || []} itineraryIdentifier="code" />
          ) : (
            <ListingsV1
              itineraries={itineraries || []}
              itineraryIdentifier="code"
            />
          ))}
      </div>
  </div>
  }
}


export default withSearchParams(ListingPage);